<script lang="ts">
    import { DEFAULT_BOX, DEFAULT_LAYOUT } from '$lib/components/constants';

    import { InputValidationType } from '$lib/types/Form';
    import { PhoneValidationKind } from '$lib/types/PhoneValidationKind';
    import { cn } from '$lib/utils/cn';

    import { Field, Form } from 'svelte-final-form';

    import Input from './Input.svelte';
    import {
        composeValidation,
        getTranslatedValidationError,
        isPublicEmailDomain,
        validationMap,
        ValidationError,
    } from './validations';
    import { getBoxAttributes } from '../_utils/getBoxAttributes';
    import Box from '../_wrappers/Box.svelte';
    import Button from '../Button/Button.View.svelte';
    import Text from '../Text/Text.View.svelte';

    import type { ButtonStyle } from '$lib/types/Components/Button/ButtonContent';
    import type { InputData } from '$lib/types/Components/FormQuestion/InputData';
    import type { Align, Box as BoxType, Size } from '$lib/types/Components/SharedTypes';
    import type { FormValues, Input as InputType } from '$lib/types/Form';
    import type { Language } from '$lib/types/Language';
    import type { TransitionEffect, Theme } from '$lib/types/Theme';
    import type { JSONContent } from '@tiptap/core';

    // Question
    export let id = '';
    export let theme: Theme;
    export let align: Align = 'center';
    export let draft: JSONContent;
    export let color = 'black';
    export let box: BoxType;
    export let onSubmit: (formValues: FormValues) => void = () => null;
    export let onMouseEnter = () => null;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let language: Language = 'en';
    export let layout = DEFAULT_LAYOUT;

    // Inputs
    export let inputs: InputData[] = [];
    export let fileUploading = false;

    // Submit Button
    export let btnId = '';
    export let btnDraft: JSONContent;
    export let btnBgColor = '#0076FF';
    export let btnColor = 'white';
    export let btnAnimation = '';
    export let btnAlign: Align = 'center';
    export let btnFullWidthOnDesktop: boolean = false;
    export let btnSize: Size = 'medium';
    export let btnStyle: ButtonStyle = 'filled';
    export let loading = false;
    export let disabled = false;

    const { isInColumn, isInMultiColumn } = layout;

    const textBox: BoxType = {
        top: 'none',
        right: 'none',
        bottom: 'large',
        left: 'none',
    };

    const generateInitialValues = (inputs: InputType[]) => {
        const values: Record<string, string> = {};

        inputs.forEach((input) => {
            values[input.name] = '';
        });

        return values;
    };

    const initialValues = generateInitialValues(inputs);

    const getValidation = ({ type, required, customOptions }: InputData) => {
        const typeToUse =
            type === 'phone' &&
            [PhoneValidationKind.CountryFormat, PhoneValidationKind.VerificationCode].includes(
                customOptions?.phoneValidationKind,
            )
                ? InputValidationType.PHONE_V2
                : type;
        const withType = (
            fn: (value: string, type: InputValidationType) => ValidationError | undefined,
        ) => {
            return (value: string) => {
                return fn(value, typeToUse);
            };
        };

        const companyEmailValidator = customOptions?.companyEmailsOnly
            ? (value: string) => {
                  if (value && isPublicEmailDomain(value)) {
                      return ValidationError.PublicEmail;
                  }
              }
            : undefined;

        const inputTypeValidator = validationMap[typeToUse]
            ? withType(validationMap[typeToUse])
            : undefined;

        const validators: ((value: string) => ValidationError | undefined)[] = [];

        if (required) {
            validators.push(withType(validationMap[InputValidationType.REQUIRED]));
        }

        if (inputTypeValidator) {
            validators.push(inputTypeValidator);
        }

        if (companyEmailValidator) {
            validators.push(companyEmailValidator);
        }

        return validators.length ? composeValidation(...validators) : undefined;
    };
</script>

<Box {id} {...getBoxAttributes({ box, isInColumn })} {transitionDelay} {transitionEffect}>
    <Text transitionEffect="none" raw={draft} {align} {color} box={textBox} {layout} />

    <Form {onSubmit} {initialValues} let:form let:state>
        <form
            action="javascript:void(0)"
            class={cn('fun-mx-auto  fun-space-y-4 sm:fun-mt-2', {
                'fun-max-w-xl': !isInMultiColumn,
            })}
            on:submit|preventDefault={form.submit}
        >
            {#each inputs as formInput (formInput.name)}
                <Field name={formInput.name} let:input let:meta validate={getValidation(formInput)}>
                    <Input
                        finalFormInput={input}
                        invalid={meta.error && meta.touched}
                        pristine={meta.pristine}
                        touched={meta.touched}
                        active={meta.active}
                        uploading={fileUploading}
                        errorMessage={getTranslatedValidationError(meta.error, language)}
                        {...formInput}
                        {language}
                        {btnBgColor}
                        {theme}
                        {form}
                    />
                </Field>
            {/each}
            <div class="fun-mt-4">
                <Button
                    id={btnId}
                    draft={btnDraft}
                    color={btnColor}
                    bgColor={btnBgColor}
                    fullWidthOnDesktop={btnFullWidthOnDesktop}
                    buttonType="submit"
                    {onMouseEnter}
                    align={btnAlign}
                    size={btnSize}
                    box={DEFAULT_BOX}
                    animation={btnAnimation}
                    buttonStyle={btnStyle}
                    {loading}
                    disabled={disabled || state.submitting}
                    {transitionEffect}
                />
            </div>
        </form>
    </Form>
</Box>
